// Title badges drawn the way the game's Unity front end draws them (Oct 19 2026; Ben's dev: "The frontend takes the grayscale
// image, adds the correct round border based on the tier, and generates the live material applied to the image (with the
// shininess, reflection and everything)"). A port of the Front End's Assets/Materials/badgeShaderLite.shadergraph,
// BadgeFromHeight.hlsl and BadgeMaterial.mat (feature/new-backend 516bd958):
//   - the title's grey height map (images/titles/badges/<name>.png on the game's host, through /api/title-art/badges/…)
//     and the tier's ring from badge_covers.png (public/badge/badge-covers.png: 5 cells, tier 1 at the top) make one height
//     field; its slope is the normal, two blurred mips give the ambient occlusion;
//   - each tier's two colours, metalness and roughness are mixed by height (tier 1 wood, 2 bronze, 3 silver, 4 gold, 5 red);
//   - one warm light (diffuse + Blinn-Phong with Schlick Fresnel) and the cloister environment reflected (Resources/Badges/
//     rx.hdr, a vertical cross, turned into a lat-long and blurred like the cubemap's mips 3 and up: public/badge/badge-env.bin,
//     half-float RGBA, 128×64 down to 1×1);
//   - the eye moves with the device tilt (_BadgeTilt), as BadgeTiltDriver feeds it in the game.
// The game's project is in gamma colour space, so every value is used as it is (no sRGB conversions).

export const VERT = `#version 300 es
in vec2 aPos;
out vec2 vUV;
void main() { vUV = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

export const FRAG = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uBg;      // the title's height map (grey)
uniform sampler2D uFrame;   // badge_covers.png: the five rings, tier 1 at the top
uniform sampler2D uEnv;     // the environment, lat-long, mip 0 = the cubemap's mip 3
uniform vec2 uBgTexel;      // 1 / height-map size
uniform vec2 uFrameTexel;   // 1 / atlas size
uniform float uTier;
uniform vec2 uTilt;         // _BadgeTilt (device tilt, smoothed)

// BadgeMaterial.mat
const vec4  BG_ST = vec4(1.0, 1.0, 0.0, 0.0);
const float FRAME_LIFT = 0.05;
const float HEIGHT_SCALE = 0.003;
const float SOFTNESS = 1.2;
const float AO_LOD = 4.63;
const float AO_GAIN = 4.0;
const float AO_DIRECT = 0.586;
const float AO_STRENGTH = 1.0;
const float ENV_INTENSITY = 1.5;
const float ENV_MAX_MIP = 6.0;
const float ENV_PITCH = 1.89;     // degrees (the graph's Rotate About Axis is set to degrees for the pitch)
const float ENV_YAW = 0.61;       // radians
const float EXPOSURE = 1.0;
const float LIGHT_INTENSITY = 1.4;
const vec3  LIGHT_COLOR = vec3(1.0, 0.9686275, 0.9019608);
const vec3  LIGHT_DIR = vec3(-0.5, 0.19, 1.71);
const float TILT_SCALE = 1.5;
const vec2  START_TILT = vec2(0.0, 0.14);
const vec2  HEIGHT_REMAP = vec2(0.0, 1.0);
const float ENV_BASE_LOD = 3.0;   // uEnv's level 0 is the cubemap's mip 3

// SG_Tier Select: < 1.5 → tier 1 … else tier 5
vec4 tierPick(vec4 t1, vec4 t2, vec4 t3, vec4 t4, vec4 t5) {
  return uTier < 1.5 ? t1 : uTier < 2.5 ? t2 : uTier < 3.5 ? t3 : uTier < 4.5 ? t4 : t5;
}
float tierPickF(float t1, float t2, float t3, float t4, float t5) {
  return uTier < 1.5 ? t1 : uTier < 2.5 ? t2 : uTier < 3.5 ? t3 : uTier < 4.5 ? t4 : t5;
}

vec4 frameST() {
  return tierPick(vec4(1.0, 0.2, 0.0, 0.8), vec4(1.0, 0.2, 0.0, 0.6), vec4(1.0, 0.2, 0.0, 0.4), vec4(1.0, 0.2, 0.0, 0.2), vec4(1.0, 0.2, 0.0, 0.0));
}

vec2 frameAtlasUV(vec2 uv, vec4 st) {
  vec2 a = uv * st.xy + st.zw;
  vec2 cellMin = st.zw + uFrameTexel * 0.5;
  vec2 cellMax = st.zw + st.xy - uFrameTexel * 0.5;
  return clamp(a, cellMin, cellMax);
}
float compositeHeight(vec2 uv, vec4 st, float bias) {
  float bgH = texture(uBg, uv * BG_ST.xy + BG_ST.zw, bias).r;
  vec4 fr = texture(uFrame, frameAtlasUV(uv, st), bias);
  return mix(bgH, fr.r + FRAME_LIFT, fr.a);
}
float compositeHeightLod(vec2 uv, vec4 st, float lod) {
  float bgH = textureLod(uBg, uv * BG_ST.xy + BG_ST.zw, lod).r;
  vec4 fr = textureLod(uFrame, frameAtlasUV(uv, st), lod);
  return mix(bgH, fr.r + FRAME_LIFT, fr.a);
}

// Rotate About Axis (Shader Graph, radians)
vec3 rotateAbout(vec3 v, vec3 axis, float r) {
  float s = sin(r), c = cos(r), oc = 1.0 - c;
  axis = normalize(axis);
  mat3 m = mat3(
    oc * axis.x * axis.x + c,          oc * axis.x * axis.y + axis.z * s, oc * axis.z * axis.x - axis.y * s,
    oc * axis.x * axis.y - axis.z * s, oc * axis.y * axis.y + c,          oc * axis.y * axis.z + axis.x * s,
    oc * axis.z * axis.x + axis.y * s, oc * axis.y * axis.z - axis.x * s, oc * axis.z * axis.z + c);
  return m * v;   // column-major mat3 of the transposed rows = HLSL mul(rot_mat, v)
}

vec3 fresnelSchlick(vec3 f0, float cosT, float rough) {
  return f0 + (max(vec3(1.0 - rough), f0) - f0) * pow(clamp(1.0 - cosT, 0.0, 1.0), 5.0);
}

vec3 envSample(vec3 d, float lod) {
  float lon = atan(d.x, d.z);
  float lat = asin(clamp(d.y, -1.0, 1.0));
  vec2 uv = vec2(lon / 6.2831853 + 0.5, 0.5 - lat / 3.1415927);
  return textureLod(uEnv, uv, max(lod - ENV_BASE_LOD, 0.0)).rgb;
}

void main() {
  vec2 UV = vUV;
  vec4 st = frameST();

  // BadgeFromHeight_float
  float softness = max(SOFTNESS, 1.0);
  float bias = log2(softness);
  vec2 frameTexel = uFrameTexel / st.xy;
  vec2 texel = max(uBgTexel, frameTexel);
  vec2 stepUV = max(texel, fwidth(UV)) * softness;
  float h  = compositeHeight(UV, st, 0.0);
  float hL = compositeHeight(UV - vec2(stepUV.x, 0.0), st, bias);
  float hR = compositeHeight(UV + vec2(stepUV.x, 0.0), st, bias);
  float hD = compositeHeight(UV - vec2(0.0, stepUV.y), st, bias);
  float hU = compositeHeight(UV + vec2(0.0, stepUV.y), st, bias);
  float slopeX = HEIGHT_SCALE * (hR - hL) / (2.0 * stepUV.x);
  float slopeY = HEIGHT_SCALE * (hU - hD) / (2.0 * stepUV.y);
  vec3 N = normalize(vec3(-slopeX, -slopeY, 1.0));
  float blurA = compositeHeightLod(UV, st, AO_LOD);
  float blurB = compositeHeightLod(UV, st, AO_LOD + 1.5);
  float AO = clamp(1.0 - max(0.5 * (blurA + blurB) - h, 0.0) * AO_GAIN, 0.0, 1.0);
  float d = length(UV - vec2(0.5)) - 0.495;
  float alpha = clamp(0.5 - d / max(fwidth(d), 1e-5), 0.0, 1.0);

  // the material, mixed by height
  float t = smoothstep(HEIGHT_REMAP.x, HEIGHT_REMAP.y, h);
  vec3 dark  = tierPick(vec4(0.06603771, 0.05088965, 0.03457635, 1.0), vec4(0.1226415, 0.08702105, 0.06768423, 1.0), vec4(0.1768868, 0.1934301, 0.2358491, 1.0), vec4(0.3098039, 0.2235294, 0.1294118, 1.0), vec4(0.1226415, 0.008751547, 0.0, 1.0)).rgb;
  vec3 light = tierPick(vec4(0.3490566, 0.2619884, 0.1662958, 1.0), vec4(0.3584906, 0.1864164, 0.09300464, 1.0), vec4(0.4589712, 0.4959773, 0.509434, 1.0), vec4(0.7254902, 0.4784314, 0.0, 1.0), vec4(0.509434, 0.1265205, 0.1225525, 1.0)).rgb;
  vec3 albedo = mix(dark, light, t);
  float metal = mix(tierPickF(0.0, 0.5, 0.6, 0.5, 0.6), tierPickF(0.1, 0.95, 0.9, 1.0, 0.8), t);
  float rough = max(mix(tierPickF(0.0, 0.5, 0.5, 0.55, 0.5), tierPickF(0.4, 0.2, 0.2, 0.2, 0.1), t), 0.5);
  vec3 F0 = mix(vec3(0.04), albedo, metal);

  // the eye moves with the tilt; one light; the environment
  vec2 tilt = uTilt * TILT_SCALE + START_TILT;
  vec3 V = normalize(vec3(tilt, 1.0));
  vec3 L = normalize(LIGHT_DIR);
  vec3 H = normalize(V + L);
  float NdotL = dot(N, L);
  vec3 lightC = LIGHT_COLOR * LIGHT_INTENSITY;
  vec3 diffuse = albedo * (1.0 - metal) * NdotL * lightC;
  float r4 = rough * rough * rough * rough;
  float specPow = max(2.0 / max(r4, 1e-4) - 2.0, 1.0);
  vec3 spec = pow(max(dot(N, H), 0.0), specPow) * fresnelSchlick(F0, dot(V, H), 0.0) * NdotL * lightC;
  vec3 R = rotateAbout(rotateAbout(reflect(-V, N), vec3(1.0, 0.0, 0.0), radians(ENV_PITCH)), vec3(0.0, 1.0, 0.0), ENV_YAW);
  vec3 env = envSample(R, rough * ENV_MAX_MIP) * ENV_INTENSITY * fresnelSchlick(F0, dot(N, V), rough);
  vec3 col = ((diffuse + spec) * mix(1.0, AO, AO_DIRECT) + env * mix(1.0, AO, AO_STRENGTH)) * EXPOSURE;
  outColor = vec4(clamp(col, 0.0, 1.0) * alpha, alpha);   // premultiplied
}`;

export interface EnvLevel { w: number; h: number; data: Uint16Array }

/** badge-env.bin: half-float RGBA levels 128×64, 64×32 … 1×1 */
export function envLevelsFrom(buf: ArrayBuffer): EnvLevel[] {
  const u16 = new Uint16Array(buf);
  const out: EnvLevel[] = [];
  let w = 128, h = 64, o = 0;
  while (o + w * h * 4 <= u16.length) {
    const n = w * h * 4;
    out.push({ w, h, data: u16.subarray(o, o + n) });
    o += n;
    if (w === 1 && h === 1) break;
    w = Math.max(1, w >> 1);
    h = Math.max(1, h >> 1);
  }
  return out;
}

type Img = HTMLImageElement | ImageBitmap;
const UNIFORMS = ["uBg", "uFrame", "uEnv", "uBgTexel", "uFrameTexel", "uTier", "uTilt"] as const;

/** One WebGL2 context drawing badges onto its canvas. Throws when WebGL2 isn't there or the shader doesn't build. */
export class BadgeGl {
  readonly gl: WebGL2RenderingContext;
  private readonly prog: WebGLProgram;
  private readonly u: Record<(typeof UNIFORMS)[number], WebGLUniformLocation | null>;
  private readonly frameTex: WebGLTexture;
  private readonly frameSize: [number, number];
  private readonly envTex: WebGLTexture;
  private readonly bg = new Map<Img, WebGLTexture>();

  constructor(readonly canvas: HTMLCanvasElement | OffscreenCanvas, frame: Img, env: EnvLevel[]) {
    const gl = canvas.getContext("webgl2", { premultipliedAlpha: true, alpha: true, antialias: false, preserveDrawingBuffer: true }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error("WebGL2 isn't available");
    this.gl = gl;
    const shader = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "badge shader");
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, shader(gl.VERTEX_SHADER, VERT));
    gl.attachShader(p, shader(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "badge program");
    this.prog = p;
    gl.useProgram(p);
    const b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(p, "aPos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.u = Object.fromEntries(UNIFORMS.map((n) => [n, gl.getUniformLocation(p, n)])) as BadgeGl["u"];
    this.frameTex = this.image(frame);
    this.frameSize = [frame.width, frame.height];
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    env.forEach((lv, i) => gl.texImage2D(gl.TEXTURE_2D, i, gl.RGBA16F, lv.w, lv.h, 0, gl.RGBA, gl.HALF_FLOAT, lv.data));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, env.length - 1);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.envTex = t;
  }

  /** an image as a mipmapped texture, bottom row first (Unity's v = 0 is the bottom) */
  private image(img: Img): WebGLTexture {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  /** Draws one badge over the whole canvas. tier 1–5 (above 5 draws as 5); tilt as the game's _BadgeTilt. */
  draw(height: Img, tier: number, tilt: [number, number] = [0, 0]): void {
    const gl = this.gl;
    let bg = this.bg.get(height);
    if (!bg) {
      bg = this.image(height);
      this.bg.set(height, bg);
    }
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.prog);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, bg);
    gl.uniform1i(this.u.uBg, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.frameTex);
    gl.uniform1i(this.u.uFrame, 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.envTex);
    gl.uniform1i(this.u.uEnv, 2);
    gl.uniform2f(this.u.uBgTexel, 1 / height.width, 1 / height.height);
    gl.uniform2f(this.u.uFrameTexel, 1 / this.frameSize[0], 1 / this.frameSize[1]);
    gl.uniform1f(this.u.uTier, tier);
    gl.uniform2f(this.u.uTilt, tilt[0], tilt[1]);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** Lets go of the GPU memory (a live badge closing). */
  dispose(): void {
    const ext = this.gl.getExtension("WEBGL_lose_context");
    ext?.loseContext();
  }
}
